import { useState } from 'react';
import StaffNotation from '@/components/feature/StaffNotation';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import { CLEF_QUESTIONS, type ClefQuestion } from '@/data/clefQuiz';
import { MASTERY_ACCURACY_GOAL } from '@/lib/progress/gating';

interface ClefQuizProps {
  onAnswer?: (correct: boolean) => void;
  /** chamado ao terminar a verificação: `true` quando atingiu a meta de domínio */
  onComplete?: (passed: boolean, correct: number, total: number) => void;
  /** conjunto de perguntas; sem ele, usa as perguntas da clave de sol */
  questions?: ClefQuestion[];
}

/**
 * Verificação interativa curta das aulas de explicação (clave, linhas e
 * espaços, sinais, acidentes...). A aula só é dominada quando o aluno acerta
 * todas as perguntas — abrir a página ou apertar Play não conta.
 */
export default function ClefQuiz({ onAnswer, onComplete, questions = CLEF_QUESTIONS }: ClefQuizProps) {
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [finished, setFinished] = useState(false);
  const [passed, setPassed] = useState(false);

  const question = questions[index];
  const total = questions.length;
  const answered = selected !== null;

  const choose = (optionIndex: number) => {
    if (answered) return;
    setSelected(optionIndex);
    const isCorrect = optionIndex === question.answerIndex;
    if (isCorrect) setCorrectCount((value) => value + 1);
    else setWrongCount((value) => value + 1);
    onAnswer?.(isCorrect);
  };

  const next = () => {
    if (index + 1 >= total) {
      const finalAccuracy = total > 0 ? Math.round((correctCount / total) * 100) : 0;
      const didPass = finalAccuracy >= MASTERY_ACCURACY_GOAL;
      setPassed(didPass);
      setFinished(true);
      onComplete?.(didPass, correctCount, total);
      return;
    }
    setIndex((value) => value + 1);
    setSelected(null);
  };

  const restart = () => {
    setIndex(0);
    setSelected(null);
    setCorrectCount(0);
    setWrongCount(0);
    setFinished(false);
    setPassed(false);
  };

  if (finished) {
    const accuracy = Math.round((correctCount / total) * 100);
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
        <span
          className={`mx-auto w-16 h-16 flex items-center justify-center rounded-full ${
            passed ? 'bg-accent-100 text-accent-600' : 'bg-secondary-100 text-secondary-700'
          }`}
        >
          <i className={`${passed ? 'ri-medal-line' : 'ri-question-line'} text-3xl`} />
        </span>
        <h3 className="mt-5 font-heading text-xl font-bold text-foreground-950">
          {passed ? 'Aula dominada!' : 'Quase! Verificação incompleta'}
        </h3>
        <p className="mt-2 text-sm text-foreground-700">
          {passed
            ? `Você alcançou ${accuracy}% de precisão. A próxima aula foi desbloqueada.`
            : `Você acertou ${correctCount} de ${total} (${accuracy}%). A meta de domínio é ${MASTERY_ACCURACY_GOAL}%. Revise e tente novamente.`}
        </p>
        <div className="mt-6 flex justify-center">
          <Button variant="primary" icon="ri-refresh-line" onClick={restart}>
            Refazer verificação
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <Badge tone="primary" icon="ri-question-line">
          Pergunta {index + 1} de {total}
        </Badge>
        <span className="text-xs font-label font-semibold text-foreground-500">
          Acertos: {correctCount} · Erros: {wrongCount}
        </span>
      </div>

      <p className="mt-3 rounded-md bg-primary-50 px-3 py-2 text-xs text-foreground-700">
        Meta de domínio: {MASTERY_ACCURACY_GOAL}% de precisão nesta verificação.
      </p>

      <div className="mt-4 flex min-h-[104px] flex-col items-center justify-center gap-1 rounded-lg bg-background-100 p-3 md:p-4">
        {question.glyph ? (
          <>
            <span className="font-heading text-4xl font-bold tracking-wide text-foreground-950">
              {question.glyph}
            </span>
            {question.glyphHint ? (
              <span className="font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
                {question.glyphHint}
              </span>
            ) : null}
          </>
        ) : (
          <StaffNotation notes={['G4']} space={24} />
        )}
      </div>

      <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
        {question.question}
      </h3>

      <div className="mt-4 grid grid-cols-1 gap-3">
        {question.options.map((option, optionIndex) => {
          const isAnswer = optionIndex === question.answerIndex;
          const isChosen = selected === optionIndex;
          let classes =
            'border-background-300 bg-background-50 hover:bg-background-100 text-foreground-900';
          if (answered && isAnswer) classes = 'border-accent-400 bg-accent-100 text-accent-900';
          else if (answered && isChosen) classes = 'border-secondary-400 bg-secondary-100 text-secondary-900';
          else if (answered) classes = 'border-background-200 bg-background-50 text-foreground-500';
          return (
            <button
              key={option}
              type="button"
              disabled={answered}
              onClick={() => choose(optionIndex)}
              className={`flex items-center justify-between rounded-md border px-4 py-3 text-left text-sm font-label font-semibold transition-colors ${classes} ${
                answered ? 'cursor-default' : 'cursor-pointer'
              }`}
            >
              <span>{option}</span>
              {answered && isAnswer ? (
                <i className="ri-check-line w-5 h-5 flex items-center justify-center" />
              ) : null}
              {answered && isChosen && !isAnswer ? (
                <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
              ) : null}
            </button>
          );
        })}
      </div>

      {answered ? (
        <div className="mt-4 rounded-lg bg-background-100 px-4 py-3 text-sm text-foreground-800 animate-pop">
          {question.explanation}
        </div>
      ) : null}

      {answered ? (
        <div className="mt-5 flex justify-end">
          <Button variant="primary" icon="ri-arrow-right-line" onClick={next}>
            {index + 1 >= total ? 'Concluir' : 'Próxima pergunta'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}